'use client';

import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { CURRENCY_COOKIE, LOCALE_COOKIE } from '~/constants/cookies';
import { TRPC_SETTINGS } from '~/constants/trpc';
import type { Locale } from '~/i18n/i18n-config';
import { i18n } from '~/i18n/i18n-config';
import { api } from '~/libs/api';
import { useTripBookingStore } from '~/stores/tpv2/tripBookingStore';
import { getCookie, setSiteCookie } from '~/utils/cookies';
import type { CurrencyName } from '~/utils/currencyHelpers';
import {
  CURRENCIES,
  createCurrencyFormatter,
  getDisplayCurrencyCookie,
  isValidCurrency
} from '~/utils/currencyHelpers';

type ContextType = {
  displayCurrency: CurrencyName;
  setDisplayCurrency: null | ((currency: CurrencyName) => void);
  convertPriceInGBP: (amount: number, showDecimals?: boolean, outputCurrency?: CurrencyName) => string;
};

export const defaultContextVals = {
  displayCurrency: CURRENCIES[0],
  setDisplayCurrency: null,
  convertPriceInGBP: (_amount: number, _showDecimals?: boolean, _outputCurrency?: CurrencyName) => 'not initialised'
};

export const CurrencyContext = createContext<ContextType>(defaultContextVals);

export const CurrencyContextProvider = CurrencyContext.Provider;

export function CurrencyState({
  children,
  initialCurrency
}: Readonly<{ children: ReactNode; initialCurrency?: CurrencyName }>) {
  const currencyContext = useContext(CurrencyContext);

  if (!currencyContext) {
    throw new Error('useCurrency must be used within a Provider');
  }
  const [displayCurrency, setDisplayCurrency] = useState<CurrencyName>(initialCurrency ?? 'GBP');

  const { data, isLoading } = api.account.getCurrentExchangeRates.useQuery(
    {},
    {
      ...TRPC_SETTINGS.QUERY_NO_REFETCH
    }
  );

  const exchangeRateData = data?.exchangeRates;

  const setCurrencyCookieAndState = (currency: CurrencyName) => {
    setSiteCookie(CURRENCY_COOKIE, currency);
    setDisplayCurrency(currency);
    useTripBookingStore.getState().initializeCurrency(currency);
  };

  // converts a GBP amount to the display currency (unless otherwise specified) using current exchange rates
  const convertPriceInGBP = (amount: number, showDecimals = false, outputCurrency = displayCurrency) => {
    if (isLoading || !exchangeRateData) return '';
    if (!isValidCurrency(outputCurrency)) throw new Error('Invalid currency');

    const exchangeRate = exchangeRateData.find(
      (exchangeObj) => exchangeObj.quoteCurrency === outputCurrency
    )?.exchangeRate;

    const convertedAmount = exchangeRate ? amount * exchangeRate : amount;

    const userLocale = (getCookie(LOCALE_COOKIE) as Locale) || i18n.defaultLocale;
    const currencyFormatter = createCurrencyFormatter(userLocale, outputCurrency, showDecimals);

    return currencyFormatter.format(convertedAmount);
  };

  useEffect(() => {
    const currencyCookie = getDisplayCurrencyCookie();
    if (currencyCookie && isValidCurrency(currencyCookie)) setDisplayCurrency(currencyCookie);
  }, []);

  return (
    <CurrencyContextProvider
      value={{
        displayCurrency,
        setDisplayCurrency: setCurrencyCookieAndState,
        convertPriceInGBP
      }}
    >
      {children}
    </CurrencyContextProvider>
  );
}

export function useCurrencyContext() {
  const currencyContext = useContext<ContextType>(CurrencyContext);
  return currencyContext;
}
